import React, { useState, useEffect, useRef } from 'react';

/**
 * Non-blocking processing status component.
 * Renders as a floating banner at the bottom of the screen instead of a blocking modal.
 * Progress only moves forward (monotonic) and animates smoothly.
 *
 * Props:
 *   isProcessing - boolean, show/hide
 *   progress     - number 0-100, actual progress from backend
 *   message      - string, current stage description
 *   stage        - string key for current stage (optional, for stage indicators)
 *   onCancel     - function, called when cancel is clicked (optional)
 *   variant      - "banner" (default, non-blocking) or "overlay" (legacy blocking)
 */
const ProcessingStatus = ({
  isProcessing,
  progress = 0,
  message = "Processing...",
  stage = null,
  onCancel = null,
  variant = "banner"
}) => {
  // Monotonic progress - only goes up, never down
  const [displayProgress, setDisplayProgress] = useState(0);
  const animationRef = useRef(null);
  const [isMinimized, setIsMinimized] = useState(false);

  // Reset when processing starts
  useEffect(() => {
    if (isProcessing) {
      setDisplayProgress(0);
      setIsMinimized(false);
    }
  }, [isProcessing]);

  // Smooth monotonic progress animation
  useEffect(() => {
    if (!isProcessing) return;

    const target = Math.max(progress, displayProgress);
    if (target <= displayProgress) return;

    // Animate from current to target
    const startValue = displayProgress;
    const diff = target - startValue;
    const duration = 400; // ms
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const fraction = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - fraction, 3);
      const newValue = startValue + diff * eased;

      setDisplayProgress(newValue);

      if (fraction < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [progress, isProcessing]);

  if (!isProcessing) return null;

  const roundedProgress = Math.round(displayProgress);

  // Stage indicators
  const stages = {
    'init': 'Initializing',
    'scg_retrieval': 'Retrieving Rules',
    'classification': 'Classifying',
    'sanitization': 'Sanitizing',
    'redaction': 'Redacting',
    'analysis': 'Analyzing',
    'finalization': 'Finalizing'
  };

  if (variant === "overlay") {
    // Legacy blocking overlay mode (for pages that need it)
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="vault-card-elevated rounded-xl p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="relative mb-6 flex justify-center">
              <img
                src="/DECLASS GIF.gif"
                alt="Processing"
                className="w-20 h-20 object-contain"
              />
            </div>
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[var(--text-secondary)]">Progress</span>
                <span className="text-sm font-medium text-[var(--spotlight-gold)]">{roundedProgress}%</span>
              </div>
              <div className="w-full bg-[var(--vault-border)] rounded-full h-3 overflow-hidden">
                <div
                  className="bg-[var(--spotlight-gold)] h-3 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${roundedProgress}%` }}
                />
              </div>
            </div>
            <p className="text-sm text-[var(--text-muted)] mb-4">{message}</p>
            {onCancel && (
              <button
                onClick={onCancel}
                className="text-sm text-[var(--error)] hover:text-red-300 underline"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Non-blocking floating banner (default)
  if (isMinimized) {
    return (
      <div
        className="fixed bottom-4 right-4 z-40 cursor-pointer"
        onClick={() => setIsMinimized(false)}
      >
        <div className="bg-[var(--spotlight-gold)] text-[var(--vault-bg)] rounded-full px-4 py-2 shadow-lg flex items-center space-x-2 hover:bg-[var(--spotlight-amber)] transition-colors">
          <div className="w-4 h-4 border-2 border-[var(--vault-bg)] border-t-transparent rounded-full animate-spin" />
          <span className="text-sm font-medium">{roundedProgress}%</span>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-40 w-full max-w-lg px-4">
      <div className="vault-card-elevated rounded-lg overflow-hidden">
        {/* Progress bar at top of banner */}
        <div className="h-1.5 bg-[var(--vault-border)]">
          <div
            className="h-full bg-[var(--spotlight-gold)] transition-all duration-300 ease-out"
            style={{ width: `${roundedProgress}%` }}
          />
        </div>

        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              {/* Spinner */}
              <div className="flex-shrink-0">
                <div className="w-8 h-8 border-3 border-[var(--vault-border)] border-t-[var(--spotlight-gold)] rounded-full animate-spin" />
              </div>

              {/* Message and progress */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-[var(--text-primary)] truncate">{message}</p>
                  <span className="text-sm font-semibold text-[var(--spotlight-gold)] ml-2 flex-shrink-0">
                    {roundedProgress}%
                  </span>
                </div>

                {/* Stage indicators */}
                {stage && stages[stage] && (
                  <div className="flex items-center space-x-1 mt-1">
                    {Object.entries(stages).map(([key, label]) => {
                      const stageKeys = Object.keys(stages);
                      const currentIdx = stageKeys.indexOf(stage);
                      const thisIdx = stageKeys.indexOf(key);
                      const isComplete = thisIdx < currentIdx;
                      const isCurrent = key === stage;

                      return (
                        <div
                          key={key}
                          className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
                            isComplete
                              ? 'bg-[var(--success)]'
                              : isCurrent
                                ? 'bg-[var(--spotlight-gold)]'
                                : 'bg-[var(--vault-border)]'
                          }`}
                          title={label}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center space-x-1 ml-3 flex-shrink-0">
              <button
                onClick={() => setIsMinimized(true)}
                className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] rounded"
                title="Minimize"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {onCancel && (
                <button
                  onClick={onCancel}
                  className="p-1 text-[var(--text-muted)] hover:text-[var(--error)] rounded"
                  title="Cancel"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcessingStatus;
